import { createClient, gql } from 'urql'
import {
	ConsolidatedPractice,
	ConsolidatedPracticeWhereInput,
	Mutation,
	Query,
	QueryMode
} from './generated-types/custom-graphql'
import { gqlClientOptions } from './base'
import { GraphQLClientOptions } from './graphql-custom-types'

export const UPDATE_CONSOLIDATED_PRACTICE_STATUS = gql<Mutation>(`
  mutation updateConsolidatedPracticeStatus(
    $id: String!,
    $processType: String,
    $processStatus: String,
    ) {
      updateConsolidatedPracticeStatus(
      id: $id,
      processType: $processType,
      processStatus: $processStatus,
      ) {
        id
        status
    }
  }
`)

export const GET_CONSOLIDATED_PRACTICES_SLIM_QUERY = gql`
	query GetConsolidatedPracticesSlim(
		$where: ConsolidatedPracticeWhereInput
		$orderBy: [ConsolidatedPracticeOrderByInput!]
		$skip: Int
		$take: Int
	) {
		consolidatedPractices(where: $where, orderBy: $orderBy, skip: $skip, take: $take) {
			total
			items {
				id
				paymentId
				lendingId
				name
				consolidatorId
				consolidatorReferenceCode
				consolidatorReferenceCode2
				regionalManagerId
				logoUrl
				city
				state
				country
				isActive
				terminalLocation
			}
		}
	}
`

export const GET_CONSOLIDATED_PRACTICES_QUERY = gql`
	query GetConsolidatedPractices(
		$where: ConsolidatedPracticeWhereInput
		$orderBy: [ConsolidatedPracticeOrderByInput!]
		$skip: Int
		$take: Int
	) {
		consolidatedPractices(where: $where, orderBy: $orderBy, skip: $skip, take: $take) {
			total
			items {
				id
				paymentId
				lendingId
				consolidatorReferenceCode
				consolidatorReferenceCode2
				consolidatorReferenceCode3
				isPaymentEnabled
				isLendingEnabled
				isRebateEnabled
				isActive
				isDeleted
				name
				legalBusinessName
				consolidatorId
				consolidator {
					id
					featureFlags
				}
				address
				address2
				email
				signerEmail
				signerName
				phone
				city
				state
				zip
				country
				currency
				timezone
				settings
				pimsMetadata
				activeTerminalsCount
				totalTerminalsCount
				terminalLocation
				hospitalManagerEmail
				website
				regionalManagerId
				surchargeEnabled
				surchargeFeePct
				surchargeStartDate
				transactionFees
				integrationId
				netsuiteId
				skyworldId
				logoUrl
				isCustomPayoutEnabled
				customPayoutTarget
				customPayoutSettings
				customPayoutRunAtTime
				customPayoutStartedAt
				createdAt
				updatedAt
			}
		}
	}
`

export const UPSERT_CONSOLIDATED_PRACTICE = gql<Mutation>(`
  mutation upsertConsolidatedPractice(
    $id: String,
    $name: String,
    $legalBusinessName: String,
    $address: String,
    $address2: String,
    $city: String,
    $state: String,
    $zip: String,
    $country: String,
    $currency: String,
    $paymentId: String,
    $lendingId: Int,
    $email: String,
    $phone: String,
    $signerName: String,
    $signerEmail: String,
    $isDeleted: Boolean,
    $isLendingEnabled: Boolean,
    $isPaymentEnabled: Boolean,
    $isRebateEnabled: Boolean,
    $consolidatorId: String,
    $timezone: String,
    $pimsMetadata: JSON,
    $settings: JSON
  ) {
    upsertConsolidatedPractice(
      id: $id,
      name: $name,
      legalBusinessName: $legalBusinessName,
      address: $address,
      address2: $address2,
      city: $city,
      state: $state,
      zip: $zip,
      country: $country,
      currency: $currency,
      paymentId: $paymentId,
      lendingId: $lendingId,
      email: $email,
      phone: $phone,
      signerName: $signerName,
      signerEmail: $signerEmail,
      isDeleted: $isDeleted,
      isLendingEnabled: $isLendingEnabled,
      isPaymentEnabled: $isPaymentEnabled,
      isRebateEnabled: $isRebateEnabled,
      consolidatorId: $consolidatorId,
      timezone: $timezone,
      pimsMetadata: $pimsMetadata,
      settings: $settings
    ) {
      id
      name
      legalBusinessName
      city
      state
      country
      isLendingEnabled
      isPaymentEnabled
      isRebateEnabled
      createdAt
      updatedAt
    }
  }
`)

export const UPSERT_CONSOLIDATED_PRACTICE_BANK_ACCOUNT_MUTATION = gql`
	mutation UpsertConsolidatedPracticeBankAccount(
		$id: String
		$consolidatedPracticeId: String
		$accountNumber: String
		$routingNumber: String
		$accountHolderName: String
		$accountHolderType: String
		$bankName: String
		$status: BankAccountStatus
		$usedForProduct: [ScratchProduct!]
		$stripeBankAccountId: String
		$stripePaymentMethodId: String
		$stripeCustomerId: String
		$stripePlatformAccountId: String
		$metadata: JSON
		$disabledReason: String
	) {
		upsertConsolidatedPracticeBankAccount(
			id: $id
			consolidatedPracticeId: $consolidatedPracticeId
			accountNumber: $accountNumber
			routingNumber: $routingNumber
			accountHolderName: $accountHolderName
			accountHolderType: $accountHolderType
			bankName: $bankName
			status: $status
			usedForProduct: $usedForProduct
			stripeBankAccountId: $stripeBankAccountId
			stripePaymentMethodId: $stripePaymentMethodId
			stripeCustomerId: $stripeCustomerId
			stripePlatformAccountId: $stripePlatformAccountId
			metadata: $metadata
			disabledReason: $disabledReason
		) {
			id
			consolidatedPracticeId
			accountNumber
			routingNumber
			accountHolderName
			accountHolderType
			bankName
			status
			usedForProduct
			stripeBankAccountId
			stripePaymentMethodId
			stripeCustomerId
			stripePlatformAccountId
			metadata
			enabledAt
			enabledByUserId
			disabledAt
			disabledByUserId
			disabledReason
			createdAt
			updatedAt
		}
	}
`

export const LIST_CONSOLIDATED_PRACTICES_AND_BANK_ACCOUNTS = gql`
	query GetConsolidatedPracticesAndBankAccounts(
		# Consolidated Practices filters
		$wherePractices: ConsolidatedPracticeWhereInput
		$orderByPractices: [ConsolidatedPracticeOrderByInput!]
		$skipPractices: Int
		$takePractices: Int
		# Bank Accounts filters
		$consolidatedPracticeId: String!
		$whereBankAccounts: ConsolidatedPracticeBankAccountWhereInput
		$orderByBankAccounts: ConsolidatedPracticeBankAccountOrderByInput
		$skipBankAccounts: Int
		$takeBankAccounts: Int
	) {
		consolidatedPractices(
			where: $wherePractices
			orderBy: $orderByPractices
			skip: $skipPractices
			take: $takePractices
		) {
			total
			items {
				id
				paymentId
				lendingId
				isPaymentEnabled
				isLendingEnabled
				isRebateEnabled
				isDeleted
				name
				legalBusinessName
				consolidatorId
				consolidatorReferenceCode
				consolidatorReferenceCode2
				consolidatorReferenceCode3
				address
				address2
				email
				signerEmail
				signerName
				phone
				city
				state
				zip
				country
				currency
				timezone
				website
				hospitalManagerEmail
				regionalManagerId
				createdAt
				updatedAt
				pimsMetadata
			}
		}

		practiceBankAccounts(
			consolidatedPracticeId: $consolidatedPracticeId
			where: $whereBankAccounts
			orderBy: $orderByBankAccounts
			skip: $skipBankAccounts
			take: $takeBankAccounts
		) {
			total
			items {
				id
				consolidatedPracticeId
				accountNumber
				routingNumber
				accountHolderName
				accountHolderType
			}
		}
	}
`

export const LIST_PRACTICE_BANK_ACCOUNTS = gql`
	query GetPracticeBankAccounts(
		$consolidatedPracticeId: String!
		$whereBankAccounts: ConsolidatedPracticeBankAccountWhereInput
		$orderByBankAccounts: ConsolidatedPracticeBankAccountOrderByInput
		$skipBankAccounts: Int
		$takeBankAccounts: Int
	) {
		practiceBankAccounts(
			consolidatedPracticeId: $consolidatedPracticeId
			where: $whereBankAccounts
			orderBy: $orderByBankAccounts
			skip: $skipBankAccounts
			take: $takeBankAccounts
		) {
			total
			items {
				id
				consolidatedPracticeId
				accountNumber
				routingNumber
				accountHolderName
				accountHolderType
			}
		}
	}
`

/**
 * Consolidated practices with the fields the custom payout flows need. Searching this table rather
 * than `practices` keeps the lookup to one round trip and returns the payout columns the backend
 * actually gates on — the `practice` table has its own copies of `isCustomPayoutEnabled` and
 * `timezone` that can drift out of step with these.
 */
export const SEARCH_CONSOLIDATED_PRACTICES_FOR_PAYOUT_QUERY = gql<Query>(`
  query SearchConsolidatedPracticesForPayout ($where: ConsolidatedPracticeWhereInput, $take: Int) {
    consolidatedPractices (where: $where, take: $take, orderBy: { name: asc }) {
      items {
        id
        paymentId
        lendingId
        name
        consolidatorId
        timezone
        isCustomPayoutEnabled
        customPayoutTarget
        customPayoutSettings
      }
    }
  }
`)

export const getConsolidatedPracticeById = async (
	graphQLClientOptions: GraphQLClientOptions,
	id: string
): Promise<ConsolidatedPractice | null> => {
	const client = createClient(gqlClientOptions(graphQLClientOptions))

	const response = await client
		.query<{
			consolidatedPractices: { items: ConsolidatedPractice[] }
		}>(GET_CONSOLIDATED_PRACTICES_QUERY, {
			where: { id: { equals: id } },
			take: 1
		})
		.toPromise()

	if (response.error) {
		throw response.error
	}

	return response?.data?.consolidatedPractices?.items?.[0] ?? null
}

/** How many rows the admin practice pickers offer per search. */
const SEARCH_RESULT_LIMIT = 20

/**
 * Matches whichever id or name the admin typed. A numeric input is a vet clinic id; anything else
 * could be a name, a `prc_` id or an `acct_` Stripe account id, so all three are tried at once.
 */
const buildSearchWhere = (input: string): ConsolidatedPracticeWhereInput =>
	/^\d+$/.test(input)
		? { lendingId: { equals: parseInt(input, 10) } }
		: {
				OR: [
					{ name: { contains: input, mode: 'insensitive' } },
					{ paymentId: { contains: input } },
					{ id: { contains: input } }
				]
			}

/**
 * Type-ahead search over the consolidated practices, used by the admin practice pickers.
 *
 * @param graphQLClientOptions - Request level (and practice/consolidator scope) for the gql client
 * @param input - What the admin typed: a name fragment, a vet clinic id, a `prc_` id or an `acct_` id
 * @param additionalWhere - ANDed with the typed-input match, for callers that can only act on a
 *   subset of practices (e.g. custom payouts, which needs a Stripe account on the row)
 * @returns Up to {@link SEARCH_RESULT_LIMIT} matching practices with the custom payout fields,
 *   ordered by name
 */
export const searchConsolidatedPractices = async (
	graphQLClientOptions: GraphQLClientOptions,
	input: string,
	additionalWhere?: ConsolidatedPracticeWhereInput
): Promise<ConsolidatedPractice[]> => {
	const client = createClient(gqlClientOptions(graphQLClientOptions))
	const match = buildSearchWhere(input)

	const result = await client
		.query(SEARCH_CONSOLIDATED_PRACTICES_FOR_PAYOUT_QUERY, {
			where: additionalWhere ? { AND: [additionalWhere, match] } : match,
			take: SEARCH_RESULT_LIMIT
		})
		.toPromise()

	if (result.error) {
		throw result.error
	}

	return result.data?.consolidatedPractices?.items ?? []
}

/** Lean practice page for the feature-flag targeting picker. */
export const LIST_CONSOLIDATED_PRACTICES_DIRECTORY_QUERY = gql<Query>(`
  query ListConsolidatedPracticesDirectory ($where: ConsolidatedPracticeWhereInput, $skip: Int, $take: Int) {
    consolidatedPractices (where: $where, orderBy: { name: asc }, skip: $skip, take: $take) {
      total
      items {
        id
        paymentId
        name
        consolidatorId
        pimsMetadata
      }
    }
  }
`)

export type PracticeDirectoryScope =
	| { kind: 'consolidator'; consolidatorId: string }
	| { kind: 'independent' }
	/** Whole catalog — how a typed search is answered */
	| { kind: 'all' }

export interface PracticeDirectoryFilters {
	scope: PracticeDirectoryScope
	/** Matched against name, `prc_` id and `acct_` paymentId; empty = no text filter */
	search: string
	/** A `Pims.currentPimsValue`; empty = every PIMS */
	pims: string
}

export interface PracticeDirectoryPage {
	items: ConsolidatedPractice[]
	total: number
}

const directoryWhere = ({
	scope,
	search,
	pims
}: PracticeDirectoryFilters): ConsolidatedPracticeWhereInput => {
	const term = search.trim()
	return {
		isDeleted: { equals: false },
		...(scope.kind === 'consolidator' ? { consolidatorId: { equals: scope.consolidatorId } } : {}),
		...(scope.kind === 'independent' ? { consolidatorId: { equals: null } } : {}),
		...(pims ? { pimsMetadata: { path: ['currentPims'], equals: pims } } : {}),
		...(term
			? {
					OR: [
						{ name: { contains: term, mode: QueryMode.Insensitive } },
						{ id: { equals: term } },
						{ paymentId: { equals: term } }
					]
				}
			: {})
	}
}

/**
 * One page of practices for the targeting picker, filtered server-side (DASH-9329: the unpaged
 * catalog fetch used to time the service out).
 * @param filters - Scope (consolidator / independent / all) plus text and PIMS filters
 * @param skip - 0-based offset
 * @param take - Page size
 * @returns The page and the total the filters match
 */
export const listConsolidatedPracticesDirectoryPage = async (
	graphQLClientOptions: GraphQLClientOptions,
	filters: PracticeDirectoryFilters,
	skip: number,
	take: number
): Promise<PracticeDirectoryPage> => {
	const client = createClient(gqlClientOptions(graphQLClientOptions))

	const result = await client
		.query<Query>(LIST_CONSOLIDATED_PRACTICES_DIRECTORY_QUERY, {
			where: directoryWhere(filters),
			skip,
			take
		})
		.toPromise()

	if (result.error) {
		throw result.error
	}

	return {
		items: result.data?.consolidatedPractices?.items ?? [],
		total: result.data?.consolidatedPractices?.total ?? 0
	}
}

export interface DirectoryLoadProgress {
	loaded: number
	total: number
}

const WALK_PAGE_SIZE = 500

/** Backstop against a bogus `total` driving an unbounded walk. */
const WALK_MAX_PAGES = 100

/**
 * Walks one group to the end, for "select every practice in this group".
 * @param filters - The group to walk and the filters in force
 * @param onProgress - Called after each page
 * @returns Every practice in the group, deduped by id, plus the total the service reported
 */
export const listAllPracticesInGroup = async (
	graphQLClientOptions: GraphQLClientOptions,
	filters: PracticeDirectoryFilters,
	onProgress?: (progress: DirectoryLoadProgress) => void
): Promise<PracticeDirectoryPage> => {
	const byId = new Map<string, ConsolidatedPractice>()
	let total = 0
	let page = 0

	do {
		const { items, total: groupTotal } = await listConsolidatedPracticesDirectoryPage(
			graphQLClientOptions,
			filters,
			page * WALK_PAGE_SIZE,
			WALK_PAGE_SIZE
		)
		total = groupTotal
		// a row added or renamed mid-walk can repeat across pages
		items.forEach((practice) => byId.set(practice.id, practice))
		onProgress?.({ loaded: byId.size, total })
		if (items.length === 0) break
		page += 1
	} while (page * WALK_PAGE_SIZE < total && page < WALK_MAX_PAGES)

	// the service total survives a walk cut short by the page cap, so a caller can tell the two apart
	return { items: [...byId.values()], total: Math.max(total, byId.size) }
}
